import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { FavoriteButtonContainer, FilledStarIcon, OutlineStarIcon } from './styles';

export interface FavoriteStarButtonProps {
  isFavorite: boolean;
  onClick: VoidFunction;
  className?: string;
  /** The toggle cannot be used right now (a request is in flight, or the state is not known yet). */
  disabled?: boolean;
}

/**
 * The star toggle for marking a resource as a favorite. Used both on the resource list row and the
 * resource details page title — both places already sit inside a larger clickable element (the row
 * card, nothing here), so clicks are stopped from bubbling before the toggle handler runs.
 */
export const FavoriteStarButton = ({
  isFavorite,
  onClick,
  className,
  disabled = false,
}: FavoriteStarButtonProps): JSX.Element => {
  const intl = useIntl();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onClick();
    },
    [onClick],
  );

  const label = intl.formatMessage({ id: isFavorite ? 'remove_from_favorites' : 'add_to_favorites' });

  return (
    <FavoriteButtonContainer
      type="button"
      className={className}
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={isFavorite}
      data-testid="favorite-star-button"
    >
      {isFavorite ? (
        <FilledStarIcon data-testid="favorite-star-filled" />
      ) : (
        <OutlineStarIcon data-testid="favorite-star-outline" />
      )}
    </FavoriteButtonContainer>
  );
};
